import React, { useCallback, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Tooltip } from 'react-tooltip';
import smsgatewayService from '../../../../../services/smsgateway.service';
import { toast } from 'react-toastify';

function formatPhoneNumber(phoneNumberString) {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phoneNumberString;
}

function normalizePhoneNumber(phoneNumber) {
    return ('' + phoneNumber).replace(/\D/g, '');
}

function handleTrim(str, len) {
    if (!str) return '';
    return str.length > len ? str.substring(0, len) + '...' : str;
}

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.trim().charAt(0).toUpperCase();
}

function getAvatarStyle(name) {
    const colors = [
        { bg: '#e8f4fd', text: '#0d6efd' },
        { bg: '#d1f2eb', text: '#198754' },
        { bg: '#cff4fc', text: '#0dcaf0' },
        { bg: '#fff3cd', text: '#ffc107' },
        { bg: '#f8d7da', text: '#dc3545' },
    ];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
}

function ComposePatientModal({ show, onHide, clinicId, onPatientSelect }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSearchTerm, setActiveSearchTerm] = useState('');
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasNext, setHasNext] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const observer = useRef();

    const resetSearchState = () => {
        setPatients([]);
        setHasNext(false);
        setNextCursor(null);
    };

    const handleClose = () => {
        setSearchTerm('');
        setActiveSearchTerm('');
        resetSearchState();
        onHide();
    };

    const searchPatients = (cursor = null, patientName = activeSearchTerm, size = 20) => {
        if (!patientName.trim()) {
            toast.info('Enter a patient name to search');
            return;
        }

        if (cursor === null) {
            setLoading(true);
            resetSearchState();
        } else {
            setLoadingMore(true);
        }

        smsgatewayService.findLegacyEyeconXPatients(clinicId, patientName.trim(), cursor, size)
            .then((res) => {
                const pageData = res.data?.data || [];
                if (cursor === null) {
                    setPatients(pageData);
                } else {
                    setPatients((prevPatients) => [...prevPatients, ...pageData]);
                }
                setHasNext(Boolean(res.data?.hasNext));
                setNextCursor(res.data?.nextCursor || null);
            })
            .catch(() => {
                toast.error('Error searching patients');
            })
            .finally(() => {
                setLoading(false);
                setLoadingMore(false);
            });
    };

    const handleSearch = () => {
        setActiveSearchTerm(searchTerm);
        searchPatients(null, searchTerm);
    };

    const handleLoadMore = useCallback(() => {
        if (hasNext && !loadingMore && nextCursor) {
            searchPatients(nextCursor, activeSearchTerm);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNext, loadingMore, nextCursor, activeSearchTerm]);

    const lastPatientElementRef = useCallback((node) => {
        if (loadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasNext) {
                handleLoadMore();
            }
        });
        if (node) observer.current.observe(node);
    }, [loadingMore, hasNext, handleLoadMore]);

    const handlePatientClick = (patient) => {
        const phone = normalizePhoneNumber(patient.cellNumber || patient.phoneNumber);
        if (!phone) {
            toast.error('Selected patient does not have a valid phone number');
            return;
        }

        onPatientSelect({
            ...patient,
            phone,
        });
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" scrollable>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="h5">
                    <i className="bi bi-pencil-square me-2 text-primary"></i>
                    Compose Message
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
                <p className="text-muted small mb-3">
                    Search by first name, last name, full name (e.g. John Smith), or phone number.
                </p>
                <div className="input-group input-group-sm mb-3">
                    <span className="input-group-text bg-light border-end-0">
                        <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                        type="text"
                        className="form-control form-control-sm border-start-0 bg-light"
                        placeholder="Search by name or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />
                    <button className="btn btn-primary btn-sm text-white" type="button" onClick={handleSearch}>
                        <i className="bi bi-search text-white me-1"></i>
                        Search
                    </button>
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        type="button"
                        onClick={handleClose}
                        title="Close"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                <div className="contact-list-container" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted small mt-2 mb-0">Searching patients...</p>
                        </div>
                    ) : patients.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="bi bi-person-lines-fill text-muted" style={{ fontSize: '2rem' }}></i>
                            <p className="text-muted mt-2 mb-0">
                                {activeSearchTerm ? 'No patients found' : 'Search to find a patient'}
                            </p>
                        </div>
                    ) : (
                        <div className="list-group list-group-flush">
                            {patients.map((patient, index) => {
                                const phone = patient.cellNumber || patient.phoneNumber;
                                const fullName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
                                const avatarStyle = getAvatarStyle(fullName);

                                return (
                                    <div
                                        key={`${patient.id}-${index}`}
                                        ref={patients.length === index + 1 ? lastPatientElementRef : null}
                                        className="list-group-item list-group-item-action border-0 rounded mb-1 p-3"
                                        onClick={() => handlePatientClick(patient)}
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            borderLeft: '3px solid transparent',
                                        }}
                                        data-tooltip-id="composePatientName"
                                        data-tooltip-content={fullName}
                                        data-tooltip-place="right"
                                    >
                                        <div className="d-flex align-items-start">
                                            <div className="position-relative me-3">
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle"
                                                    style={{
                                                        width: '48px',
                                                        height: '48px',
                                                        backgroundColor: avatarStyle.bg,
                                                    }}
                                                >
                                                    <span className="fw-bold" style={{ color: avatarStyle.text }}>
                                                        {getInitials(fullName)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex-fill" style={{ minWidth: 0 }}>
                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                    <h6 className="mb-0 fw-semibold text-truncate" style={{ color: '#212529' }}>
                                                        {handleTrim(fullName, 25)}
                                                    </h6>
                                                </div>
                                                <div className="small text-muted mb-1">
                                                    <i className="bi bi-telephone me-1"></i>
                                                    {formatPhoneNumber(phone)}
                                                </div>
                                                {patient.dob && (
                                                    <p className="mb-0 small text-muted text-truncate">
                                                        <i className="bi bi-calendar3 me-1"></i>
                                                        {patient.dob}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {loadingMore && (
                        <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted small mt-2 mb-0">Loading more...</p>
                        </div>
                    )}
                </div>
                <Tooltip id="composePatientName" style={{ zIndex: 9999 }} />
            </Modal.Body>

            <style>{`
                .contact-list-container::-webkit-scrollbar {
                    width: 4px;
                }
                .contact-list-container::-webkit-scrollbar-track {
                    background: transparent;
                }
                .contact-list-container::-webkit-scrollbar-thumb {
                    background: #dee2e6;
                    border-radius: 4px;
                }
                .contact-list-container::-webkit-scrollbar-thumb:hover {
                    background: #adb5bd;
                }
            `}</style>
        </Modal>
    );
}

export default ComposePatientModal;
